import {
  HTMLAttributes,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import styled from "styled-components";
import { useImages } from "hooks/image";
import parseAPNG, { APNG } from "apng-js";
import Player from "apng-js/types/library/player";
import { useDebounceCallback, useEventListener } from "usehooks-ts";

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Canvas = styled.canvas<{ $scale: number }>`
  transform: scale(${(props) => props.$scale});
  transform-origin: left top;
`;

export type ApngPlayerProps = HTMLAttributes<HTMLDivElement> & {
  src: string;
  delay?: number;
  numPlays?: number;
  rate?: number;
  yoyo?: boolean;
  showAtOnce?: boolean;
  hideOnEnd?: boolean;
  onEnd?: () => void;
};

export type ApngPlayerHandle = {
  play: () => void;
  stop: () => void;
  pause: () => void;
};

export const ApngPlayer = forwardRef((props: ApngPlayerProps, ref) => {
  const {
    src,
    delay = 0,
    numPlays = 1,
    rate = 1,
    yoyo = false,
    onEnd,
    style,
    showAtOnce,
    hideOnEnd,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scale, setScale] = useState(1);
  const [opacity, setOpacity] = useState(showAtOnce ? 1 : 0);
  const playerRef = useRef<Player>();
  const { getArrayBuffer } = useImages();
  const countRef = useRef(0);
  const apngRef = useRef<APNG>();
  const endRef = useRef(false);

  useImperativeHandle(ref, () => {
    return {
      play() {
        playerRef.current?.play();
      },
      stop() {
        playerRef.current?.stop();
      },
      pause() {
        playerRef.current?.pause();
      }
    }
  }, []);

  const debOpacity = useDebounceCallback(() => {
    setOpacity(1);
  }, Math.max(delay, 0.1) * 1000);

  const handleResize = () => {
    if (!!canvasRef.current && !!containerRef.current) {
      apngRef.current = parseAPNG(getArrayBuffer(src)) as APNG;
      if(!apngRef.current?.frames) return;
      canvasRef.current.width = Math.max(...apngRef.current.frames.map(frame => frame.width));
      canvasRef.current.height = Math.max(...apngRef.current.frames.map(frame => frame.height));
      const context = canvasRef.current?.getContext("2d");
      if (context) {
        apngRef.current.getPlayer(context).then(p => {
          playerRef.current = p;
          debOpacity();
        });
      }
      setScale(
        Math.min(
          containerRef.current?.clientWidth / canvasRef.current.width,
          containerRef.current?.clientHeight / canvasRef.current.height
        )
      );
    }
  };

  useEffect(() => {
    handleResize();
  }, []);

  const debounceResize = useDebounceCallback(handleResize, 250);
  useEventListener('resize', debounceResize);

  useEffect(() => {
    if(!opacity || !apngRef.current?.frames?.length) return;

    let time = performance.now();
    const totalFrames = apngRef.current?.frames.length || 0;
    const playRate = (apngRef.current?.playTime || 0) / rate;
    const delta = playRate / totalFrames;
    let playForward = true;
    let request = 0;

    const renderPrevFrame = () => {
      if(!playerRef.current) return;

      let n = playerRef.current.currentFrameNumber - 1; 
      playerRef.current.stop();
      while (playerRef.current.currentFrameNumber < n){
        playerRef.current.renderNextFrame();
      }
    };

    const checkEnd = (player: Player) => {
      if(++countRef.current < numPlays || !numPlays){
        playForward = true;
        if(!yoyo) player.stop();
      }else{
        if(onEnd) onEnd();
        endRef.current = true;
      }
    };

    const update = () => {
      if(endRef.current) return;

      const now = performance.now();
      const n = time + delta;

      if(!playerRef.current || now < n) {
        request = requestAnimationFrame(update);
        return;
      }

      time = n;
      const currentFrameNumber = playerRef.current.currentFrameNumber || 0;

      if(playForward){
        if(currentFrameNumber < totalFrames - 1){
          playerRef.current.renderNextFrame();
        }else{
          if(yoyo){
            playForward = false;
          }else{
            checkEnd(playerRef.current);
          }
        }
      }else{
        if(currentFrameNumber > 0){
          renderPrevFrame();
        }else{
          checkEnd(playerRef.current);
        }
      }

      if(endRef.current){
        setOpacity(hideOnEnd ? 0 : 1);
      }else{
        request = requestAnimationFrame(update);
      }
    };

    request = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(request);
    };
  }, [hideOnEnd, numPlays, onEnd, opacity, rate, yoyo]);

  return (
    <Container ref={containerRef} style={{...style, opacity}}>
      <Canvas ref={canvasRef} $scale={scale}></Canvas>
    </Container>
  );
});
